/**
 * EnvironmentSystem - Real-time Day/Night Cycle, Weather, and Four Seasons Engine.
 * Supports Dynamic Point Lighting (destination-out lantern/torch punching),
 * weather particles (Rain, Splash, Snow, Thunderstorm, Fog), and seasonal ambient particles (Cherry Blossom, Autumn Leaves).
 */

import { EntityRegistry } from '../../core/EntityRegistry';
import { EntityState } from '../../core/Types';
import { EventBus } from '../../core/EventBus';
import { getBiomeAt, getBiomeInfo, BiomeType } from './BiomeSystem';
import { WorldClock } from './WorldClock';

export type TimeOfDay = 'day' | 'dusk' | 'night' | 'dawn';
export type WeatherType = 'clear' | 'rain' | 'thunderstorm' | 'snow' | 'fog';
export type SeasonType = 'spring' | 'summer' | 'autumn' | 'winter';

export interface WeatherParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  length?: number;
  alpha: number;
  color: string;
  type: 'rain' | 'splash' | 'snow' | 'blossom' | 'leaf' | 'wind' | 'heat_wave' | 'firefly';
  rotation?: number;
  vRot?: number;
  waveOffset?: number;
}

export interface FogPuff {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  speed: number;
}

export class EnvironmentSystem {
  private timeOfDay: TimeOfDay = 'day';
  private weather: WeatherType = 'clear';
  private season: SeasonType = 'spring';
  private lastBiome: BiomeType = 'plains';
  private useWorldClock = true; // MMO 실시간 월드 클럭 동기화 플래그
  private currentFractionalHour = 12.0; // 0.0 ~ 24.0 부드러운 시간 블렌딩

  public getFractionalHour(): number {
    return this.currentFractionalHour;
  }

  public setFractionalHour(h: number): void {
    this.currentFractionalHour = (h % 24 + 24) % 24;
  }

  // Weather particles
  private weatherParticles: WeatherParticle[] = [];
  private fogPuffs: FogPuff[] = [];

  // Lightning effect for thunderstorm
  private lightningTimer = 0;
  private lightningFlashAlpha = 0;

  // Off-screen canvas for dynamic dark lighting mask with point lights
  private lightMaskCanvas: HTMLCanvasElement | null = null;
  private lightMaskCtx: CanvasRenderingContext2D | null = null;

  constructor() {
    this.initFog();
  }

  public getTimeOfDay(): TimeOfDay {
    return this.timeOfDay;
  }

  public getWeather(): WeatherType {
    return this.weather;
  }

  public getSeason(): SeasonType {
    return this.season;
  }

  public setWeatherAndSeason(weather: WeatherType, season: SeasonType): void {
    this.weather = weather;
    this.season = season;
    this.weatherParticles = [];
  }

  // --- Cycle Toggles ---
  public cycleTimeOfDay(): TimeOfDay {
    const list: TimeOfDay[] = ['day', 'dusk', 'night', 'dawn'];
    const nextIdx = (list.indexOf(this.timeOfDay) + 1) % list.length;
    this.timeOfDay = list[nextIdx];

    const labels: Record<TimeOfDay, string> = {
      day: '☀️ 한낮 (Day)',
      dusk: '🌅 노을/석양 (Dusk)',
      night: '🌙 한밤 (Midnight)',
      dawn: '🌄 새벽/여명 (Dawn)',
    };

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `⏳ [시간대 변경] ${labels[this.timeOfDay]} 모드로 전환되었습니다.`,
      category: 'system',
    });

    return this.timeOfDay;
  }

  public cycleWeather(): WeatherType {
    const list: WeatherType[] = ['clear', 'rain', 'thunderstorm', 'snow', 'fog'];
    const nextIdx = (list.indexOf(this.weather) + 1) % list.length;
    this.weather = list[nextIdx];
    this.weatherParticles = [];

    const labels: Record<WeatherType, string> = {
      clear: '🌤️ 맑음 (Clear)',
      rain: '🌧️ 비 (Rain)',
      thunderstorm: '⛈️ 뇌우/번개 (Thunderstorm)',
      snow: '❄️ 눈/눈보라 (Snow)',
      fog: '🌫️ 안개 (Fog)',
    };

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🌦️ [날씨 변경] ${labels[this.weather]} 날씨로 전환되었습니다.`,
      category: 'system',
    });

    return this.weather;
  }

  public cycleSeason(playerX?: number, playerY?: number): SeasonType {
    if (playerX !== undefined && playerY !== undefined) {
      const info = getBiomeInfo(playerX, playerY);
      if (info.fixedSeason) {
        this.season = info.fixedSeason;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚠️ [${info.name}] 이 지역은 특성상 ${info.fixedSeason === 'summer' ? '여름만' : '겨울만'} 영구히 지속되는 지형입니다!`,
          category: 'system',
        });
        return this.season;
      }
      if (info.isIndoor) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `🏰 [던전 내부] 실내 지하 던전에서는 지상 계절의 영향을 받지 않습니다.`,
          category: 'system',
        });
        return this.season;
      }
    }

    const list: SeasonType[] = ['spring', 'summer', 'autumn', 'winter'];
    const nextIdx = (list.indexOf(this.season) + 1) % list.length;
    this.season = list[nextIdx];

    // Clear previous seasonal particles on transition
    this.weatherParticles = this.weatherParticles.filter(
      (p) => p.type !== 'blossom' && p.type !== 'leaf' && p.type !== 'wind' && p.type !== 'heat_wave' && p.type !== 'firefly'
    );

    const labels: Record<SeasonType, string> = {
      spring: '🌸 봄 (Spring - 벚꽃 잎 날림)',
      summer: '🌿 여름 (Summer - 한낮 아지랑이 & 밤 반딧불이)',
      autumn: '🍁 가을 (Autumn - 붉은 단풍잎 비산)',
      winter: '⛄ 겨울 (Winter - 서리 낀 겨울빛 & 2배 강화 찬바람)',
    };

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🍂 [계절 변경] ${labels[this.season]} 계절로 전환되었습니다.`,
      category: 'system',
    });

    return this.season;
  }

  /**
   * Monitor player movement across biomes and enforce geographic seasonal rules
   */
  public checkBiomeTransition(playerX: number, playerY: number): void {
    const biome = getBiomeAt(playerX, playerY);
    if (biome !== this.lastBiome) {
      const prev = this.lastBiome;
      this.lastBiome = biome;
      const info = getBiomeInfo(playerX, playerY);

      if (info.fixedSeason) {
        this.season = info.fixedSeason;
        this.weatherParticles = this.weatherParticles.filter(
          (p) => p.type !== 'blossom' && p.type !== 'leaf' && p.type !== 'wind' && p.type !== 'heat_wave' && p.type !== 'firefly'
        );
      }

      if (biome === 'desert') {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `🗺️ [지역 진입] ${info.icon} [${info.name}] 에 진입했습니다! (${info.description})`,
          category: 'system',
        });
      } else if (biome === 'glacier') {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `🗺️ [지역 진입] ${info.icon} [${info.name}] 에 진입했습니다! (${info.description})`,
          category: 'system',
        });
      } else if (biome === 'dungeon' && prev !== 'dungeon') {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `🏰 [던전 진입] 고대 크립트 지하 던전에 들어왔습니다. 지상 날씨와 소리가 차단됩니다.`,
          category: 'system',
        });
      } else if (biome === 'plains' && prev !== 'plains') {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `🌿 [초원 복귀] 솔라리아 중앙 구릉지로 돌아왔습니다. 4계절이 모두 존재합니다.`,
          category: 'system',
        });
      }
    }
  }

  private initFog(): void {
    // 22 Soft, translucent volumetric fog puffs for gentle thin atmospheric mist
    this.fogPuffs = [];
    for (let i = 0; i < 22; i++) {
      this.fogPuffs.push({
        x: Math.random() * 2400 - 600,
        y: Math.random() * 1400 - 300,
        radius: 180 + Math.random() * 220,
        alpha: 0.10 + Math.random() * 0.08,
        speed: 8 + Math.random() * 12,
      });
    }
  }

  /**
   * Update weather particles, lightning, and season ambient
   */
  public update(dt: number, screenW: number, screenH: number): void {
    // 0. Sync TimeOfDay, Season, Fractional Hour, and Weather with the MMO World Clock
    if (this.useWorldClock) {
      const clock = WorldClock.now();
      this.currentFractionalHour = clock.fractionalHour;
      if (this.timeOfDay !== clock.timeOfDay) {
        this.timeOfDay = clock.timeOfDay;
      }
      if (this.season !== clock.season) {
        this.season = clock.season;
      }
      if (this.weather !== clock.weather) {
        this.weather = clock.weather;
        this.weatherParticles = [];
      }
    }

    // 1. Lightning in thunderstorm
    if (this.weather === 'thunderstorm') {
      this.lightningTimer += dt;
      if (this.lightningTimer > 3.5 + Math.random() * 4.0) {
        this.lightningTimer = 0;
        this.lightningFlashAlpha = 0.85; // Bright flash
        EventBus.emit('THUNDER_STRIKE', { intensity: 1.0 });
      }
    }
    if (this.lightningFlashAlpha > 0) {
      this.lightningFlashAlpha -= dt * 3.5;
      if (this.lightningFlashAlpha < 0) this.lightningFlashAlpha = 0;
    }

    // 2. Spawn and update Rain particles
    if (this.weather === 'rain' || this.weather === 'thunderstorm') {
      const spawnCount = this.weather === 'thunderstorm' ? 8 : 5;
      for (let i = 0; i < spawnCount; i++) {
        this.weatherParticles.push({
          x: Math.random() * (screenW + 400) - 200,
          y: -20,
          vx: -180 + (Math.random() - 0.5) * 40,
          vy: 650 + Math.random() * 200,
          size: 1.5,
          length: 14 + Math.random() * 8,
          alpha: 0.5 + Math.random() * 0.35,
          color: '#bae6fd',
          type: 'rain',
        });
      }
    }

    // 3. Spawn and update Snow particles (Only during snow weather)
    if (this.weather === 'snow') {
      this.weatherParticles.push({
        x: Math.random() * (screenW + 200) - 100,
        y: -10,
        vx: -25 + Math.random() * 50,
        vy: 60 + Math.random() * 60,
        size: 2.0 + Math.random() * 2.5,
        alpha: 0.6 + Math.random() * 0.4,
        color: '#ffffff',
        type: 'snow',
        rotation: Math.random() * Math.PI * 2,
        vRot: (Math.random() - 0.5) * 2,
      });
    }

    // 4. Winter Clear Day Chilly Wind Gusts (2배 강화된 매서운 겨울바람 돌풍)
    if (this.season === 'winter' && this.weather === 'clear') {
      const currentWindCount = this.weatherParticles.filter((p) => p.type === 'wind').length;
      if (currentWindCount < 16 && Math.random() < 0.22) {
        const startY = Math.random() * (screenH + 100) - 50;
        const baseSpeed = 420 + Math.random() * 260;
        const count = Math.random() < 0.65 ? 2 : 1; // 2줄기씩 무리지어 부는 2배 강풍
        for (let i = 0; i < count; i++) {
          this.weatherParticles.push({
            x: -140 - i * 50,
            y: startY + (Math.random() - 0.5) * 35,
            vx: baseSpeed + (Math.random() - 0.5) * 50,
            vy: -15 + Math.random() * 30,
            size: 1.5 + Math.random() * 1.2,
            length: 80 + Math.random() * 90,
            alpha: 0.5 + Math.random() * 0.35,
            color: Math.random() > 0.3 ? '#e0f2fe' : '#bae6fd',
            type: 'wind',
          });
        }
      }
    }

    // 5. Summer Ambient: Heat Shimmer Waves (Day) & Fireflies (Night)
    if (this.season === 'summer' && (this.weather === 'clear' || this.weather === 'fog')) {
      // 1) 한낮/노을: 지면에서 이글이글 피어오르는 열기 아지랑이
      if (this.timeOfDay === 'day' || this.timeOfDay === 'dusk') {
        const heatCount = this.weatherParticles.filter((p) => p.type === 'heat_wave').length;
        if (heatCount < 22 && Math.random() < 0.35) {
          this.weatherParticles.push({
            x: Math.random() * (screenW + 100) - 50,
            y: screenH - Math.random() * 280,
            vx: (Math.random() - 0.5) * 15,
            vy: -45 - Math.random() * 35,
            size: 1.5 + Math.random() * 1.5,
            length: 25 + Math.random() * 30,
            alpha: 0.4 + Math.random() * 0.3,
            color: 'rgba(254, 240, 138, 0.45)', // 따스하고 투명한 열파동
            type: 'heat_wave',
            waveOffset: Math.random() * Math.PI * 2,
          });
        }
      }

      // 2) 한밤/노을/새벽: 풀숲과 모닥불 주변을 맴도는 낭만적인 여름 반딧불이
      if (this.timeOfDay === 'night' || this.timeOfDay === 'dusk' || this.timeOfDay === 'dawn') {
        const fireflyCount = this.weatherParticles.filter((p) => p.type === 'firefly').length;
        if (fireflyCount < 16 && Math.random() < 0.18) {
          this.weatherParticles.push({
            x: Math.random() * screenW,
            y: screenH - 80 - Math.random() * (screenH * 0.7),
            vx: (Math.random() - 0.5) * 20,
            vy: (Math.random() - 0.5) * 16,
            size: 2.2 + Math.random() * 1.8,
            alpha: 0.65 + Math.random() * 0.35,
            color: Math.random() > 0.4 ? '#fef08a' : '#bef264', // 골드 / 라임그린 발광
            type: 'firefly',
            waveOffset: Math.random() * Math.PI * 2,
          });
        }
      }
    }

    // 6. Spawn Seasonal Blossom (Spring) / Autumn Leaves (Autumn) - tuned to gentle density (50% reduction)
    const maxSeasonalParticles = 16;
    const currentSeasonalCount = this.weatherParticles.filter((p) => p.type === 'blossom' || p.type === 'leaf').length;

    if (this.season === 'spring' && currentSeasonalCount < maxSeasonalParticles && Math.random() < 0.125) {
      this.weatherParticles.push({
        x: Math.random() * (screenW + 200) - 100,
        y: -10,
        vx: 30 + Math.random() * 40,
        vy: 40 + Math.random() * 50,
        size: 3.5 + Math.random() * 2.0,
        alpha: 0.7 + Math.random() * 0.3,
        color: Math.random() > 0.4 ? '#fbcfe8' : '#f472b6', // Pink cherry petals
        type: 'blossom',
        rotation: Math.random() * Math.PI * 2,
        vRot: (Math.random() - 0.5) * 3,
      });
    } else if (this.season === 'autumn' && currentSeasonalCount < maxSeasonalParticles && Math.random() < 0.125) {
      this.weatherParticles.push({
        x: Math.random() * (screenW + 200) - 100,
        y: -10,
        vx: 40 + Math.random() * 50,
        vy: 55 + Math.random() * 60,
        size: 4.0 + Math.random() * 3.0,
        alpha: 0.75 + Math.random() * 0.25,
        color: Math.random() > 0.5 ? '#f97316' : (Math.random() > 0.5 ? '#ef4444' : '#eab308'), // Red/Orange maple leaves
        type: 'leaf',
        rotation: Math.random() * Math.PI * 2,
        vRot: (Math.random() - 0.5) * 2.5,
      });
    }

    // 7. Update and filter particles
    this.weatherParticles = this.weatherParticles.filter((p) => {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.rotation !== undefined && p.vRot !== undefined) {
        p.rotation += p.vRot * dt;
      }

      // Wind gust decay
      if (p.type === 'wind') {
        p.alpha -= dt * 0.30;
        return p.x <= screenW + 200 && p.alpha > 0;
      }

      // Heat shimmer wave upward rise and sinusoidal wobble
      if (p.type === 'heat_wave') {
        if (p.waveOffset !== undefined) {
          p.waveOffset += dt * 4.5;
          p.x += Math.sin(p.waveOffset) * 20 * dt;
        }
        p.alpha -= dt * 0.38;
        return p.alpha > 0;
      }

      // Firefly wandering drift
      if (p.type === 'firefly') {
        if (p.waveOffset !== undefined) {
          p.waveOffset += dt * 2.2;
          p.vx += Math.cos(p.waveOffset) * 14 * dt;
          p.vy += Math.sin(p.waveOffset * 1.3) * 10 * dt;
        }
        p.alpha -= dt * 0.08;
        return p.alpha > 0 && p.x >= -50 && p.x <= screenW + 50 && p.y >= -50 && p.y <= screenH + 50;
      }

      // Rain splash generation
      if (p.type === 'rain' && p.y >= screenH - 50 && Math.random() < 0.3) {
        // Spawn small ground splash
        this.weatherParticles.push({
          x: p.x,
          y: p.y,
          vx: (Math.random() - 0.5) * 40,
          vy: -30 - Math.random() * 30,
          size: 1.5,
          alpha: 0.6,
          color: '#e0f2fe',
          type: 'splash',
        });
        return false;
      }

      if (p.type === 'splash') {
        p.alpha -= dt * 2.5;
        return p.alpha > 0;
      }

      return p.y <= screenH + 20 && p.x >= -300 && p.x <= screenW + 300;
    });

    // 6. Update Fog puffs
    if (this.weather === 'fog') {
      for (const puff of this.fogPuffs) {
        puff.x += puff.speed * dt;
        if (puff.x > screenW + puff.radius) {
          puff.x = -puff.radius * 2;
        }
      }
    }
  }

  /**
   * Render screen-space weather particles (Rain, Snow, Splashes, Petals, Leaves, Heat Waves, Fireflies, Fierce Wind)
   */
  public renderWeatherEffects(ctx: CanvasRenderingContext2D, screenW: number, screenH: number): void {
    // 1. Soft, Uniform Thin Ambient Fog across entire screen (줌인/줌아웃 시에도 완벽히 균일한 얇은 안개)
    if (this.weather === 'fog') {
      ctx.save();

      // 1) 화면 전체를 감싸는 얇고 은은한 대기 안개 틴트 (Thin Atmospheric Fog Overlay)
      // 모든 필드, 캐릭터, 몬스터, 스킬이 뚜렷하게 잘 보이면서도 몽환적인 얇은 안개 분위기 연출
      ctx.fillStyle = 'rgba(218, 226, 236, 0.16)';
      ctx.fillRect(0, 0, screenW, screenH);

      // 2) 은은하게 지면에 낮게 감도는 얇은 지면 미스트 (Subtle Low Ground Mist)
      const groundMist = ctx.createLinearGradient(0, screenH - 160, 0, screenH);
      groundMist.addColorStop(0, 'rgba(218, 226, 236, 0)');
      groundMist.addColorStop(1, 'rgba(218, 226, 236, 0.14)');
      ctx.fillStyle = groundMist;
      ctx.fillRect(0, screenH - 160, screenW, 160);

      // 3) 화면 위를 부드럽게 천천히 흘러가는 얇고 투명한 3D 안개 퍼프 (Gentle Floating Puffs)
      for (const puff of this.fogPuffs) {
        const grad = ctx.createRadialGradient(puff.x, puff.y, 15, puff.x, puff.y, puff.radius);
        grad.addColorStop(0, `rgba(241, 245, 249, ${puff.alpha})`);
        grad.addColorStop(0.6, `rgba(226, 232, 240, ${puff.alpha * 0.5})`);
        grad.addColorStop(1, 'rgba(203, 213, 225, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(puff.x, puff.y, puff.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // 2. Summer Daytime Radiant Sunbeams (Godrays)
    if (this.season === 'summer' && this.timeOfDay === 'day' && this.weather === 'clear') {
      ctx.save();
      const sunGrad = ctx.createLinearGradient(screenW, 0, screenW * 0.35, screenH);
      sunGrad.addColorStop(0, 'rgba(254, 240, 138, 0.13)');
      sunGrad.addColorStop(0.6, 'rgba(253, 224, 71, 0.05)');
      sunGrad.addColorStop(1, 'rgba(254, 240, 138, 0)');
      ctx.fillStyle = sunGrad;
      ctx.fillRect(0, 0, screenW, screenH);
      ctx.restore();
    }

    // 3. Weather & Seasonal Ambient particles
    if (this.weatherParticles.length > 0) {
      ctx.save();
      for (const p of this.weatherParticles) {
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;

        if (p.type === 'rain') {
          ctx.strokeStyle = p.color;
          ctx.lineWidth = p.size;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + (p.vx * 0.025), p.y + (p.length || 14));
          ctx.stroke();
        } else if (p.type === 'splash') {
          ctx.beginPath();
          ctx.ellipse(p.x, p.y, p.size * 2, p.size, 0, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.type === 'snow') {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.type === 'blossom' || p.type === 'leaf') {
          ctx.save();
          ctx.translate(p.x, p.y);
          if (p.rotation) ctx.rotate(p.rotation);
          ctx.beginPath();
          ctx.ellipse(0, 0, p.size * 1.4, p.size * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        } else if (p.type === 'wind') {
          // 2x Fierce Winter Wind Gust with Frost Spark
          ctx.strokeStyle = p.color;
          ctx.lineWidth = p.size;
          ctx.lineCap = 'round';
          ctx.beginPath();
          const len = p.length || 90;
          ctx.moveTo(p.x, p.y);
          ctx.quadraticCurveTo(p.x + len * 0.5, p.y - 6, p.x + len, p.y + 3);
          ctx.stroke();

          // Frost glint at wind tip
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(p.x + len, p.y + 3, 1.2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.type === 'heat_wave') {
          // Shimmering S-curve Heat Wave rising from ground
          ctx.strokeStyle = p.color;
          ctx.lineWidth = p.size;
          ctx.lineCap = 'round';
          ctx.beginPath();
          const h = p.length || 30;
          ctx.moveTo(p.x, p.y);
          ctx.bezierCurveTo(p.x - 7, p.y - h * 0.35, p.x + 7, p.y - h * 0.7, p.x, p.y - h);
          ctx.stroke();
        } else if (p.type === 'firefly') {
          // Romantic Summer Firefly with soft pulsing glow halo
          const glow = ctx.createRadialGradient(p.x, p.y, 1, p.x, p.y, p.size * 3.5);
          glow.addColorStop(0, p.color);
          glow.addColorStop(0.4, p.color);
          glow.addColorStop(1, 'rgba(254, 240, 138, 0)');
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 3.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // 3. Thunderstorm Flash Overlay
    if (this.lightningFlashAlpha > 0) {
      ctx.save();
      ctx.fillStyle = `rgba(255, 255, 255, ${this.lightningFlashAlpha})`;
      ctx.fillRect(0, 0, screenW, screenH);
      ctx.restore();
    }
  }

  /**
   * 24시간 주기에 따라 부드럽게 보간된 조도(Ambient Light RGBA)를 반환합니다.
   * 급격한 점프 없이 서서히 밝아지고 서서히 어두워집니다.
   */
  public getAmbientLightingColor(): { r: number; g: number; b: number; a: number } {
    const h = (this.currentFractionalHour % 24 + 24) % 24;

    // 키프레임 정의 (hour, [r, g, b, alpha])
    // 00:00 - 깊은 밤 (10, 15, 30, 0.72)
    // 04:00 - 깊은 밤에서 여명의 기운 (25, 20, 50, 0.65)
    // 05:00 - 은은한 여명/보랏빛 새벽 (120, 60, 180, 0.42)
    // 06:00 - 일출 시작 (240, 140, 80, 0.22)
    // 07:00 - 아침 햇살 (250, 200, 140, 0.08)
    // 08:00 ~ 17:00 - 쾌청한 한낮 (0, 0, 0, 0.00)
    // 17:30 - 오후 늦은 햇살 (245, 170, 70, 0.06)
    // 18:30 - 노을/석양 피크 (234, 88, 12, 0.28)
    // 19:30 - 황혼 박명 (80, 30, 100, 0.48)
    // 20:30 - 밤 진입 (15, 20, 40, 0.68)
    // 24:00 - 깊은 밤 (10, 15, 30, 0.72)
    const keyframes: Array<[number, [number, number, number, number]]> = [
      [0.0,  [10, 15, 30, 0.72]],
      [4.0,  [25, 20, 50, 0.65]],
      [5.0,  [120, 60, 180, 0.42]],
      [6.0,  [240, 140, 80, 0.22]],
      [7.0,  [250, 200, 140, 0.08]],
      [8.0,  [0, 0, 0, 0.0]],
      [17.0, [0, 0, 0, 0.0]],
      [17.7, [245, 170, 70, 0.08]],
      [18.5, [234, 88, 12, 0.28]],
      [19.5, [80, 30, 100, 0.48]],
      [20.5, [15, 20, 40, 0.68]],
      [24.0, [10, 15, 30, 0.72]],
    ];

    // Find current interval
    let prev = keyframes[0];
    let next = keyframes[keyframes.length - 1];

    for (let i = 0; i < keyframes.length - 1; i++) {
      if (h >= keyframes[i][0] && h <= keyframes[i + 1][0]) {
        prev = keyframes[i];
        next = keyframes[i + 1];
        break;
      }
    }

    const t = next[0] === prev[0] ? 0 : (h - prev[0]) / (next[0] - prev[0]);
    // Smoothstep interpolation (3t^2 - 2t^3)
    const smoothT = t * t * (3 - 2 * t);

    const r = Math.round(prev[1][0] + (next[1][0] - prev[1][0]) * smoothT);
    const g = Math.round(prev[1][1] + (next[1][1] - prev[1][1]) * smoothT);
    const b = Math.round(prev[1][2] + (next[1][2] - prev[1][2]) * smoothT);
    let a = Number((prev[1][3] + (next[1][3] - prev[1][3]) * smoothT).toFixed(3));

    // 계절별 미세 색감 보정
    if (this.season === 'winter') {
      a = Math.max(a, 0.12); // 겨울의 서늘한 기운
    } else if (this.season === 'autumn') {
      a = Math.max(a, 0.06); // 가을의 황금빛 오후
    }

    return { r, g, b, a };
  }

  /**
   * Render Global Day/Night Ambient Lighting with Real-Time Point Light Punching.
   * Punches warm circular holes around torches, campfires, portal gates, and the player lantern.
   */
  public renderLightingMask(
    ctx: CanvasRenderingContext2D,
    screenW: number,
    screenH: number,
    lightPoints: Array<{ x: number; y: number; radius: number; color?: string; intensity?: number }>
  ): void {
    const ambient = this.getAmbientLightingColor();
    if (ambient.a <= 0.01) {
      return; // Fully bright daytime -> no overlay needed
    }

    // Setup off-screen lighting mask canvas for clean destination-out punching
    if (!this.lightMaskCanvas || this.lightMaskCanvas.width !== screenW || this.lightMaskCanvas.height !== screenH) {
      this.lightMaskCanvas = document.createElement('canvas');
      this.lightMaskCanvas.width = screenW;
      this.lightMaskCanvas.height = screenH;
      this.lightMaskCtx = this.lightMaskCanvas.getContext('2d');
    }

    if (!this.lightMaskCtx) return;
    const lCtx = this.lightMaskCtx;

    const baseColor = `rgba(${ambient.r}, ${ambient.g}, ${ambient.b}, ${ambient.a})`;

    // Clear and fill mask
    lCtx.clearRect(0, 0, screenW, screenH);
    lCtx.fillStyle = baseColor;
    lCtx.fillRect(0, 0, screenW, screenH);

    // Punch out point lights (Lantern, Torches, Campfire, Portal) when ambient is dark enough
    if (ambient.a >= 0.15) {
      lCtx.save();
      lCtx.globalCompositeOperation = 'destination-out';

      const darknessFactor = Math.min(1.0, (ambient.a - 0.15) / 0.5);

      for (const pt of lightPoints) {
        const rad = pt.radius;
        const grad = lCtx.createRadialGradient(pt.x, pt.y, rad * 0.1, pt.x, pt.y, rad);
        const alpha = (pt.intensity || 1.0) * (0.5 + 0.45 * darknessFactor);
        grad.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
        grad.addColorStop(0.6, `rgba(0, 0, 0, ${alpha * 0.5})`);
        grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

        lCtx.fillStyle = grad;
        lCtx.beginPath();
        lCtx.arc(pt.x, pt.y, rad, 0, Math.PI * 2);
        lCtx.fill();
      }
      lCtx.restore();

      // Add warm glow halos around lights
      lCtx.save();
      for (const pt of lightPoints) {
        const rad = pt.radius;
        const glowGrad = lCtx.createRadialGradient(pt.x, pt.y, 2, pt.x, pt.y, rad * 0.7);
        glowGrad.addColorStop(0, pt.color || 'rgba(251, 191, 36, 0.28)');
        glowGrad.addColorStop(1, 'rgba(251, 191, 36, 0)');
        lCtx.fillStyle = glowGrad;
        lCtx.beginPath();
        lCtx.arc(pt.x, pt.y, rad * 0.7, 0, Math.PI * 2);
        lCtx.fill();
      }
      lCtx.restore();
    }

    // Blit lighting mask onto main canvas
    ctx.drawImage(this.lightMaskCanvas, 0, 0);
  }
}
